/* eslint-disable no-use-before-define */
import uuid from 'uuid/v4';
import { generatePath } from 'react-router';
import { sanitizeEscape } from '@Services/HtmlSanitizeService';
import HistoryResource from '@Services/HistoryResource';
import AppStorage from '@Services/AppStorage';
import Catalyst from '@Services/Catalyst';
import Breadcrumb from '@Services/Breadcrumb';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import store from '@Store';
import { generateResultsPath, generateHeatmapPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import UserDatabases from '@Services/UserDatabases';
import HistoryHelper from '@Services/HistoryHelper';
import parseV3000SumFormula from '@Utils/React/MolFiletoFormula';
import { dispatchOpenViewSaveModal, dispatchOpenConfirmationModal, closeConfirmationModal } from '@Pages/History/HistoryAction';

import get from 'lodash/get';
import each from 'lodash/each';
import assignIn from 'lodash/assignIn';
import flow from 'lodash/fp/flow';
import groupBy from 'lodash/fp/groupBy';
import map from 'lodash/fp/map';
import toArray from 'lodash/fp/toArray';
import compact from 'lodash/fp/compact';
import sortBy from 'lodash/fp/sortBy';
import moment from 'moment-timezone';

export const getHistoryTypes = () => HistoryHelper.getHistoryTypes();

export function formatNewHistory(...args) {
  return HistoryHelper.formatNewHistory(...args);
}

export function storeRecentZeroSearch(result, historyType, factItem) {
  const currentUser = AuthUtils.getUser();
  const recentSearches = get(currentUser, 'history.recentSearches', []);

  const zeroResult = {
    context: result.payload.context,
    databaseId: result.payload.databaseId,
    queryId: result.payload.queryId,
    resultName: result.payload.resultName,
    searchId: result.payload.searchId,
    hitSetKey: result.payload.hitSetKey,
    isFromAdvancedSearch: true,
    totalResult: result.payload.resultSize,
    creationTime: new Date().valueOf(),
    queryFactItem: factItem,
    historyType,
    resultSize: result.payload.resultSize,
    userTime: moment().format('YYYY/MM/DD HH:mm:ss'),
  };

  recentSearches.push(zeroResult);
  recentSearches.sort((x, y) => x.creationTime - y.creationTime);

  AuthUtils.updateUser({ history: currentUser.history });
}

/**
 * @ngdoc method
 * @name restoreHistory
 * @description retrieves actual hitset name and hitset key for saved history item
 * */
export function restoreHistory(item) {
  return HistoryResource.restoreHistory(item)
    .then((result) => {
      // eslint-disable-next-line no-param-reassign
      item.resultName = result.resultName;
      // eslint-disable-next-line no-param-reassign
      item.hitSetKey = result.hitSetKey;
      // eslint-disable-next-line no-param-reassign
      item.resultSize = result.resultSize;

      return item;
    })
    .catch(Logger.reject);
}

export function requestSavedHistory() {
  return HistoryResource.getSavedHistory().then(processHistoryItems).catch(Logger.reject);
}

export function processHistoryItems(historyItems) {
  historyItems.forEach((item) => {
    if (item.queryFactItem) {
      // eslint-disable-next-line no-param-reassign
      item.structures = HistoryHelper.extractStructuresFromFacts(item.queryFactItem);
    }
  });

  return splitHistoryByDBs(historyItems);
}

/**
 * @ngdoc method
 * @name formatExistingHistory
 * @description format object before saving to save in BE
 * */
export function formatExistingHistory(historyItem, newSearchName) {
  // eslint-disable-next-line no-param-reassign
  historyItem.searchName = newSearchName;
  return historyItem;
}

// Resolves the active breadcrumb when no item is supplied directly.
function resolveActiveItem(item) {
  if (item) return item;
  const databaseId = AuthUtils.getUser()?.history?.viewedResultsParams?.databaseId;
  const breadcrumbs = Breadcrumb.retrieveBreadcrumbs(databaseId);
  return breadcrumbs.find((crumb) => crumb.active === true);
}

// RXFE-4947: "referrer" param was added (RXFE-4885); checking stateParams alone is not sufficient.
function hasFallbackParams(prevStateParams) {
  return !!(prevStateParams?.databaseId && prevStateParams?.context && prevStateParams?.resultName && prevStateParams?.searchId);
}

function buildBaseParams(val) {
  return {
    databaseId: val('databaseId'),
    context: val('context'),
    resultName: val('resultName'),
    searchId: val('searchId'),
    hitSetKey: val('hitSetKey'),
    fromContext: val('fromContext'),
    limitOrExclude: val('limitOrExclude'),
  };
}

function navigateToHeatmap(stateParams, val) {
  assignIn(stateParams, {
    context: 'dpitems',
    resultName: val('heatmapResultName'),
    index: val('index'),
    isRow: val('isRow'),
    isHeatmap: true,
    ascending: val('ascending'),
    sortedMatrixName: val('sortedMatrixName'),
  });
  history.push({ pathname: generateHeatmapPath(stateParams), state: stateParams });
}

function navigateToResults(stateParams, val) {
  assignIn(stateParams, { pageNumber: 1, order: val('order'), sort: val('sort') });
  history.push({ pathname: generateResultsPath(stateParams), state: stateParams });
}

export function goToResultPage(item) {
  const prevStateParams = getCombinedRouteParams(store.getState());
  // RXFE-4881: breadcrumb may not yet exist for large result sets
  const resolvedItem = resolveActiveItem(item);

  if (!resolvedItem && !hasFallbackParams(prevStateParams)) return;

  // val() picks from the resolved item when available, otherwise falls back to prevStateParams
  const val = (key) => (resolvedItem ? resolvedItem[key] : prevStateParams[key]);
  const stateParams = buildBaseParams(val);

  if (resolvedItem?.isHeatmapShown) {
    navigateToHeatmap(stateParams, val);
  } else {
    navigateToResults(stateParams, val);
  }
}

export function showStructurePopup(structure) {
  store.dispatch(
    openStructureDialog(uuid(), structure, {
      name: parseV3000SumFormula(structure),
      isLinearFormula: true,
    })
  );
}

export function openConfirmPopup(content, opts = {}) {
  return new Promise((resolve, reject) => {
    dispatchOpenConfirmationModal({
      ...content,
      ...opts,
      onClose: () => {
        reject();
        store.dispatch(closeConfirmationModal());
      },
      onConfirm: () => {
        resolve();
        store.dispatch(closeConfirmationModal());
      },
    });
  });
}

/* ----------------------- RECENT HISTORY --------------------------- */
/**
 * @ngdoc method
 * @name retrieveRecentHistory
 * @description retrieve the recent searches (including 0 results) that is in the appStorage for the current user
 * @returns {Array} all recent searches of user
 * */
export function retrieveRecentHistory() {
  return get(AuthUtils.getUser(), 'history.recentSearches', []);
}

/**
 * @ngdoc method
 * @name retrieveRecentHistoryWithoutZeroResults
 * @description retrieve the recent searches (without 0 results) that is in the appStorage for the current user
 * @returns {Array} all recent searches of user without 0 results
 * */
export function retrieveRecentHistoryWithoutZeroResults() {
  return get(AuthUtils.getUser(), 'history.recentSearches', []).filter((search) => search.resultSize !== 0);
}

/**
 * @description store breadcrumbs array in recentSearches so that they don't
 * get lost when the breadcrumbs are reset
 * */
export function storeRecentHistory(sort, order) {
  const activeDatabaseId = AuthUtils.getUser().history.viewedResultsParams.databaseId;
  const activeBreadcrumb = Breadcrumb.getActiveBreadcrumbForDb(activeDatabaseId);
  // HistoryHelper.storeRecentHistory({ ...activeBreadcrumb, sort, order });
  const historyItem = {
    ...activeBreadcrumb,
    ...(sort !== undefined && { sort }),
    ...(order !== undefined && { order }),
  };
  HistoryHelper.storeRecentHistory(historyItem);
}

/* ----------------------- SAVED HISTORY --------------------------- */

export function openSaveHistoryModal(historyItem) {
  if (AuthUtils.isAnonymousUser()) {
    const errorTitle = 'Login required';
    const errorMessage = 'Please sign-in/register to save your History.';
    return NotifyDialogsService.openModalWindow({
      title: errorTitle,
      text: errorMessage,
      cancelText: 'Cancel',
      confirmText: 'Sign in',
      type: 'info',
      backdrop: 'static',
      onConfirm: () => {
        Catalyst.trackConversionDriverClick('history:savesearch:sign-in');
        AppStorage.instance.set('scheduledHistory', historyItem);
        history.push({
          pathname: generatePath(paths.LOGIN),
        });
      },
      onCancel: () => {
        AppStorage.instance.set('scheduledHistory', null);
      },
      trackingOptions: {
        message: `${errorTitle} ${errorMessage}`,
        component: 'History',
      },
    }).result;
  }
  const promise = new Promise(() => {
    dispatchOpenViewSaveModal({ historyItem });
    AppStorage.instance.set('scheduledHistory', null);
  });
  return promise;
}

export function getSavedHistory() {
  return HistoryResource.getSavedHistory();
}

export function removeHistory(historyItem) {
  return HistoryResource.removeHistory(historyItem);
}

/**
 * @ngdoc function
 * @name splitHistoryByDBs
 * @param {array} historyItems List of history items objects
 * @description Restructures history items array into the other array of databases,
 *              where each item contains databaseName it's own list of history items.
 *              Sorts databases in alphabetical order, keeping Reaxys DB on first position.
 */
export function splitHistoryByDBs(historyItems) {
  return flow([
    groupBy('databaseId'),
    map((items, key) => {
      const name = UserDatabases.getDbName(key);
      return name
        ? {
            databaseId: key,
            priority: UserDatabases.isDefaultDatabase(key) ? '' : name.toLowerCase(),
            databaseName: name,
            items,
          }
        : null;
    }),
    toArray,
    compact,
    sortBy('priority'),
  ])(historyItems);
}

export function updateItemsComments(databaseStorage) {
  each(databaseStorage, (database) => {
    // eslint-disable-next-line no-param-reassign,no-return-assign
    each(database.items, (item) => (item.comment = sanitizeEscape(item.comment, { allowedTags: ['sub', 'sup', 'i', 'hi'] })));
  });
}
